import React from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import './changelog.css';
import { Helmet } from 'react-helmet';
import { ChangelogPane } from './ChangelogPane';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';

export function Changelog() {
    return (
        <Flex direction="column">
            <Helmet>
                <title>Changelog </title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href="https://www.commandersalt.com/faq" />
                <meta name="description" content="Changelog - Recent updates" />
                <meta name="twitter:title" content="Commandersalt.com - Changelog" />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content="Commandersalt.com - Changelog" />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content="Frequently asked questions" />
            </Helmet>
            <BreadCrumbs />
            <Flex direction="column" maxWidth={800} alignSelf={'center'} marginTop={20}>
                <ChangelogPane title="Platform Release: 05102024.2 (incremental)" isOpen={true}>
                    <span>
                        <b>
                            <i>Deployed on: 05.14.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <ul>
                            <li>
                                More tweaks to algorithm to account for pip density and generic mana quantity relative
                                to ramp and fast mana available on a given turn.
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 05102024.1 (incremental)" isOpen={true}>
                    <span>
                        <b>
                            <i>Deployed on: 05.13.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <ul>
                            <li>
                                This update has some tweaks to the manabase scoring around impact of ramp and fast mana
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 05102024" isOpen={true}>
                    <span>
                        <b>
                            <i>Deployed on: 05.10.2024</i>
                        </b>
                    </span>
                    <Text>
                        This is a large update that revolves around redoing how the manabase analysis is scored. As with
                        all of the updates of this nature, I am sure there will be a decent amount of score changes
                        across the board. I don't like seeing peoples scores go down, but there does need to be a floor,
                        unfortunately.
                        <p />
                        Hopefully the net result will be that decks were rated too highly are now rated closer to where
                        they should be. I don't expect to see much movement with the high power and cEDH decks.
                        <p />
                        If you are interested in how the card probability is determined,{' '}
                        <u>
                            <a href={'https://en.wikipedia.org/wiki/Hypergeometric_distribution'} target={'_blank'}>
                                check this out.
                            </a>
                        </u>
                        <p />
                        With that said, we now look at three things:
                        <ul>
                            <li>
                                Acceleration
                                <ul>
                                    <li>
                                        This score revolves entirely around how quickly your deck can cast things on
                                        turns 1 through 3.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Playing on curve
                                <ul>
                                    <li>
                                        For this, we look at how much of your deck can be played on a per turn basis
                                        versus what the ideal curve would be. The{' '}
                                        <i>
                                            <b>ideal curve</b>
                                        </i>{' '}
                                        follows the traditional rule of casting 100% of your one-drops on turn 1, 100%
                                        of your two-drops on turn 2, etc.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Manabase quality
                                <ul>
                                    <li>
                                        Here we are looking at all mana sources in the deck, and evaluating based on the
                                        following
                                        <ul>
                                            <li>Does it enter tapped?</li>
                                            <li>
                                                How much mana does it generate vs what is its mana value? (in other
                                                words, net mana produced)
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.8 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.26.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <ul>
                            <li>Lots of fixes for cross-dependency mapping</li>
                            <li>
                                Scoring
                                <ul>
                                    <li>Triggers for commanders are now scored much higher.</li>
                                    <li>Other synergies for commanders are also scored higher.</li>
                                    <li>Enablers are scored lower</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.7 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.25.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                        <ul>
                            <li>
                                Efficiency score now gets a bump from synergy if the synergy score meets certain
                                requirements.
                            </li>
                            <li>Recursion has been added as an additional factor for Consistency.</li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Card Scores and Categories</span>
                        <ul>
                            <li>Changes for calculations around castability requirements based on mana pips.</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.6 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.20.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                        <ul>
                            <li>
                                Stax and Taxes: the overall value of these categories relative to other categories under
                                Interaction have been lowered (sorry Winota. Your time has passed 🙁)
                            </li>
                            <li>
                                Combos: Value of combos relative to other powerlevel scores has been increased (High
                                five, Teshar!).
                            </li>
                            <li>
                                Other small tweaks to level set based on feedback I have gotten from the high table.
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.5 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.15.2024</i>
                        </b>
                    </span>
                    <Text>
                        I have noticed that some decks were getting a bit too much of a bump from the recent synergy
                        bonus changes.
                        <p />
                        This update has various fixes to try and address that.
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                        <ul>
                            <li>Interaction: the baseline threshold for this has been increased.</li>
                            <li>Synergy bonuses: bonus for synergy of all types has been decreased</li>
                        </ul>
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <ul>
                            <li>Multiple fixes for parsing types for synergy context maps</li>
                            <li>
                                More nuanced lookup for synergy for specific card effects
                                <ul>
                                    <li>
                                        Removal, stax, and taxes no longer look for synergy for their respective
                                        effects. We don't really need bonuses for Trinisphere 🤣
                                        <p />
                                        <b>Note:</b> Synergy for activation costs for removal should still be counted.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.4 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.11.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                        <ul>
                            <li>
                                Bonus for high synergy with deck commanders
                                <ul>
                                    <li>
                                        <Text>
                                            There are some high power builds that have few to none deterministic combos
                                            which have been being score unfairly low. This update attempts to correct
                                            that by giving a bonus based on the number of synergistic effects of cards
                                            that synergize with the commanders in the deck.
                                            <p />
                                            The idea is that this will help account for non-deterministic combos as well
                                            as increasing the value of high synergy lists.
                                        </Text>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                "Fringe cEDH"
                                <ul>
                                    <li>
                                        <Text>
                                            The definition of fringe as far as the is concerned has now changed. A lot
                                            of the decks that were classified as "fringe" were perfectly viable as cEDH,
                                            but had no recent meta representation. The vast majority of them are now
                                            allowed into the cEDH scores (capped at 9.2 though).
                                        </Text>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.3 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.11.2024</i>
                        </b>
                    </span>
                    <Text>
                        I have been doing multiple patch releases almost every evening to adjust scores and
                        categorizations, as these errors are much more visible now with the Upgradelizer function.
                        <p />
                        Oh yea, theres a new feature - the Upgradelizer! Its super beta, but the idea is to find cards
                        that might help you upgrade your deck. It doesn't show cards that are already in your deck
                        (duh). I have a ton of ideas for it. Eventually I want to recommend direct swaps for lower
                        quality cards in a decklist.
                        <p />
                        Anyways... this update is specific to manabase scoring.
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <ul>
                            <li>
                                Urza, Lord High Artificer style mana production effects
                                <ul>
                                    <li>
                                        The manabase scores for these decks did not reflect the value of his ability.
                                        This should also carry over to any card with similar abilities... eg tap an
                                        untapped ____ to add ___.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.2 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.02.2024</i>
                        </b>
                    </span>
                    <Text>
                        Recent changes caused the precon power levels to get pushed up into the 5 and 6 power level
                        range. This is a problem because precons are meant to represent a pretty low bar for deck power
                        levels.
                        <p />
                        This release is focused on course correcting things that were artificially inflating their
                        scores:
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Card scores</span>
                        <ul>
                            <li>Casting cost (generic and colored pips) are now more heavily weighted</li>
                            <li>Removal - scoring now reflects target type</li>
                            <li>Draw - abilities that require sacrificing itself are now scored lower</li>
                            <li>
                                Ramp - triggered abilities that generate treasure (and gold/powerstone) tokens are now
                                scored higher
                            </li>
                            <li>
                                Ramp - harsher evaluation of cost to benefit ratio when looking at mana production
                                scoring
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <ul>
                            <li>Taplands are scored more severely</li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <ul>
                            <li>Efficiency now has a higher baseline score</li>
                        </ul>
                    </Text>
                    <Text>
                        Additionally, there were some bug fixes made to the following category detection algorithms:
                    </Text>
                    <ul>
                        <li>Graveyard</li>
                        <li>Tutors</li>
                        <li>Ramp</li>
                        <li>Removal</li>
                        <li>Fast mana</li>
                    </ul>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024.1 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 04.01.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                        <ul>
                            <li>
                                The following categories have been improved:
                                <ul>
                                    <li>Ramp</li>
                                    <li>Fast mana</li>
                                </ul>
                            </li>
                            <li>Additionally, I added "Theft" as a category (previously it was only under Salt)\</li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <ul>
                            <li>Lots of fixes to relational matches of cross-dependencies between synergy maps</li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <ul>
                            <li>
                                Fixed a bug where synergy bonuses were pushing some cards above the max score per card
                            </li>
                            <li>Slightly lowered baseline scores for efficiency to match new scoring algorithm</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 03302024" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 03.30.2024</i>
                        </b>
                    </span>
                    <Text>
                        This update focuses on a complete overhaul of the card scoring system.
                        <p />
                        Additionally, we are now adding bonuses for card synergy, which is applied to deck scoring.
                        <p />
                        There is pretty much an infinite amount of things that can go wrong here, so don't be surprised
                        if you see your scores jumping around a bit over the next couple of weeks as I fine tune it :)
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 02162024.3 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 03.12.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                        <ul>
                            <li>
                                Blink
                                <ul>
                                    <li>Better aggregation of effects</li>
                                </ul>
                            </li>
                            <li>
                                Recursion
                                <ul>
                                    <li>Blink effects no longer count as recursion</li>
                                </ul>
                            </li>
                            <li>
                                Removal
                                <ul>
                                    <li>Blink effects no longer count as removal</li>
                                    <li>
                                        Effects that put -1/-1 counters on itself (such as Shield Sphere) no longer
                                        counted as removal
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Graveyard
                                <ul>
                                    <li>
                                        Effects that remove itself from a graveyard no longer count as graveyard
                                        interaction
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Draw
                                <ul>
                                    <li>Better handling of reveal effects like Atraxa, the Grand Unifier</li>
                                    <li>Better aggregation of other draw keywording</li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Card context mapping</span>
                        <ul>
                            <li>Better parsing of trigger effects with 'for each' wording</li>
                            <li>Fix for self-reference checks on flip cards</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 02162024.3 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 03.05.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Importing</span>
                        <ul>
                            <li>
                                Deckstats
                                <ul>
                                    <li>
                                        Reverted recent change to Deckstats imports - things should be working correctly
                                        again.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 02162024.2 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 02.24.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                        <ul>
                            <li>
                                Stax
                                <ul>
                                    <li>Ward effects no longer count as stax</li>
                                    <li>Auras like Pacifism are no longer counted as stax</li>
                                </ul>
                            </li>
                            <li>
                                Draw
                                <ul>
                                    <li>
                                        Reveal to hand effects like Dark Confidant and Yuriko are now counted as draw
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Evasion
                                <ul>
                                    <li>Ward effects count as evasion</li>
                                </ul>
                            </li>
                            <li>
                                Removal
                                <ul>
                                    <li>
                                        Effects that remove/exile card(s) from graveyards no longer count as removal
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Cheat
                                <ul>
                                    <li>Better handling of Cascade effects</li>
                                </ul>
                            </li>
                            <li>
                                Ramp
                                <ul>
                                    <li>Better identification of untap effects</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 02162024.1 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 02.19.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Manuel, Grader of Decks</span>
                        <ul>
                            <li>
                                Casual/Precon
                                <ul>
                                    <li>
                                        Decks are only marked as precon level now if they are power level 0-4 and have a
                                        combo score of zero
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Importing</span>
                        <ul>
                            <li>
                                TopDecked
                                <ul>
                                    <li>Fixed import issue for some decks</li>
                                </ul>
                            </li>
                            <li>
                                Archidekt
                                <ul>
                                    <li>Fixed issue with parsing categories for cards in decks</li>
                                    <li>Added support for importing decks from articles</li>
                                </ul>
                            </li>
                            <li>
                                MtGGoldfish
                                <ul>
                                    <li>Fixed issue with decks that had sideboards</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 02162024" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 02.16.2024</i>
                        </b>
                    </span>
                    <Text>
                        This one was a bit of sad release.
                        <p />
                        Long story short, I have learned a lot over the course of this project, and the way that I
                        structured the data for decks on the site has changed significantly. This resulted in my having
                        to write a lot of code to transform decks with older data formats to the latest greatest format.
                        Last Thursday I deployed an update to try and update everything all at once on the site.
                        Unfortunately I received an absolutely mammoth bill from AWS from the following 24 hours, so I
                        pulled the site down until I could find a solution.
                        <p />
                        Unfortunately, the only real solution here was to delete the site's database and start over :(
                        <p />
                        There were ~139,000 decks on the site before this... We'll get back up there eventually ;)
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 01042024.2 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 01.30.2024</i>
                        </b>
                    </span>
                    <Text>
                        This update is primarily focused on backend improvements and database optimizations.
                        <p />
                        Also, we onboarded Manuel to be our resident <b>Edgelord in Chief</b>.
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Backend</span>
                        <ul>
                            <li>Fixed aggregation of decks by commander and author</li>
                            <li>Caching improvements</li>
                            <li>General bug fixes and clean up</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 01042024.1 (incremental)" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 01.11.2024</i>
                        </b>
                    </span>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Importing</span>
                        <ul>
                            <li>
                                Capped number of cards allowed in deck lists to 110. The reason its not 100 is to allow
                                people flexibility while building their lists
                                <ul>
                                    <li>Apologies to all of you with 268+ cards in your deck… 😂</li>
                                </ul>
                            </li>
                            <li>Fixed imports of colorless decks</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <p />
                        <ul>
                            <li>* Fixed aggregation of basic fetch lands</li>
                            <li>
                                This should make people’s manafixing score and power level go up slightly if they run
                                basic fetches
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Categories</span>
                        <p />
                        <ul>
                            <li>
                                Ramp
                                <ul>
                                    <li>
                                        Cards that allow additional lands per turn, or put lands in to play are now
                                        correctly counted as ramp
                                    </li>
                                    <li>
                                        Scoring has been updated; it now looks at:
                                        <ul>
                                            <li>
                                                Play 1 (or more) additional land per turn
                                                <ul>
                                                    <li>Additional points awarded if it allows more than 1</li>
                                                </ul>
                                            </li>
                                            <li>Puts in to play tapped/untapped</li>
                                            <li>Whether or not the land searched for has to be basic</li>
                                        </ul>
                                    </li>
                                    <li>Fixed error with aggregating MDFC/transform cards</li>
                                </ul>
                            </li>
                            <li>
                                Removal
                                <ul>
                                    <li>Cards that grant Infect are no longer counted as removal</li>
                                    <li>Cards that do damage divided among targets are now counted as removal</li>
                                    <li>
                                        Added check for effects that only affect cards that aren’t of a certain type
                                    </li>
                                    <li>Added stricter scoring based on mana value</li>
                                </ul>
                            </li>
                            <li>
                                Draw
                                <ul>
                                    <li>
                                        Effects that look at the top cards of library and then puts them into your hand
                                        are now counted as card draw
                                    </li>
                                    <li>
                                        Stax effects like Spirit of the Labyrinth are no longer counted as card draw
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Stax
                                <ul>
                                    <li>
                                        Effects that restrict attacks or blocks (such as Pramikon, Sky Rampart) are now
                                        counted as stax
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Evasion
                                <ul>
                                    <li>Cards like Silence and Aurelia’s Fury are now counted as evasion</li>
                                </ul>
                            </li>
                            <li>
                                Tutor
                                <ul>
                                    <li>
                                        Fixed issue where cards with ‘search’ in their name were incorrectly counted as
                                        tutors
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <p />
                        <ul>
                            <li>Fixed trigger condition checks when there is no card type</li>
                            <ul>
                                <li>Example being Rielle’s discard trigger</li>
                            </ul>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Card Scoring</span>
                        <p />
                        <ul>
                            <li>Fixed issue where it incorrectly evaluated mana value for MDFC/transform cards</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <p />
                        <ul>
                            <li>Casual</li>
                            <ul>
                                <li>Casual decks now get a slight bump in score based on ramp</li>
                            </ul>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 01042024" isOpen={false}>
                    <span>
                        <b>
                            <i>Deployed on: 01.04.2024</i>
                        </b>
                    </span>
                    <Text>
                        This update brings a lot of changes for manabase, card, and power level scoring. The focus on
                        all of this is to more accurately suss out a deck's power level. Many decks are going to go down
                        a bit in power level because of this update. Most of this will be due to the fact that the decks
                        in question were scored too highly in the first place.
                        <p />
                        As annoying as this is,{' '}
                        <b>I would much rather have a deck be scored lower than it objectively is than higher</b>. The
                        reason being that if the site scores your deck as an 8 when it is in reality much lower, and you
                        sit down to play in a high powered pod, you are going to get stomped... And that feels bad.
                        {/*<p />I feel that the vast majority of actual commander decks out there are far below the standards*/}
                        {/*used for cEDH decks... And that is completely fine! Everyone wants a high score, of course, but in*/}
                        {/*terms of actual playability with a group of randos, lower scores tend to be more fun. And that,*/}
                        {/*after all, is the point of not only the format. In short -{' '}*/}
                        {/*<b>do not forget to have the rule zero conversation.</b>*/}
                        {/*<p />*/}
                        {/*All that being said... The power levels generated by this site are not intended to begospel, and*/}
                        {/*they are an ever changing work in progress 😊. If you feel your scores are too low (or too high!),*/}
                        {/*please drop by the discord and let me know!*/}
                    </Text>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Card scores</span>
                        <p />
                        Card scoring is now much more granular. Scoring is derived by looking at the following list:
                        <p />
                        <ul>
                            <li>
                                <li>
                                    Casting costs
                                    <ul>
                                        <li>Card CMC</li>
                                        <li>Colored pip density</li>
                                        <li>Alternative costs</li>
                                        <li>
                                            Additional costs
                                            <ul>
                                                <li>Discard</li>
                                                <li>Sacrifice</li>
                                                <li>Exile</li>
                                                <li>Pay life</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Activation costs
                                    <ul>
                                        <li>
                                            This follows the same list as Casting Costs above, in addition to:
                                            <ul>
                                                <li>Tap/untap self</li>
                                                <li>Tap/untap other</li>
                                                <li>Sacrifice self</li>
                                                <li>Sacrifice other</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                                <li>Ability type (trigger/static/etc)</li>
                            </li>
                            <li>
                                Additionally, there is a second layer of rules logic to determine points for each
                                category that is used in Power Level calculations.
                                <ul>
                                    <li>
                                        This logic varies per category, but an example would be exile vs destroy for
                                        removal cards.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                        <p />
                        The scoring for a deck's manabase has been updated/refined in the following ways:
                        <ul>
                            <li>
                                Mana Production (per card)
                                <ul>
                                    <li>Is the amount of mana easily varied (eg Gaea's Cradle)?</li>
                                    <li>Is the amount of mana variable, but not as easily?</li>
                                    <li>How much mana is produced?</li>
                                    <li>
                                        Which colors are produced?
                                        <ul>
                                            <li>
                                                This is weighted by the pip density in the deck for the colors produced
                                            </li>
                                        </ul>
                                    </li>
                                    <li>Is it your commander?</li>
                                </ul>
                            </li>
                            <li>
                                Manabase quality (per card)
                                <ul>
                                    <li>Does it enter tapped?</li>
                                    <li>Does it enter tapped unless some condition is met (eg Cinder Glade)?</li>
                                    <li>Does it need to tap to activate?</li>
                                    <li>Is it available pregame (eg Gemstone Caverns)?</li>
                                    <li>Can you exile from hand instead of casting (eg Simian Spirit Guide)?</li>
                                    <li>Does activation require targeting something (eg Deathrite Shaman)?</li>
                                    <li>Does it have conditions for untapping (eg Basalt Monolith)?</li>
                                    <li>Is it fetchable?</li>
                                    <li>How much mana is produced relative to the cmc of the card?</li>
                                </ul>
                            </li>
                            <li>
                                Mana Fixing (in terms of entire deck)
                                <ul>
                                    <li>How many fetch lands?</li>
                                    <li>How many lands are fetchable?</li>
                                    <li>
                                        Which colors are produced?
                                        <ul>
                                            <li>
                                                This is weighted by the pip density in the deck for the colors produced
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <ul>
                            <li>
                                Combos
                                <ul>
                                    <li>
                                        The scoring for combos has evolved quite a bit. We now look at:
                                        <ul>
                                            <li>How many tutors are available for the cards in the combo</li>
                                            <li>Castability of cards in combo</li>
                                            <li>Number of cards in combo</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Baseline thresholds
                                <ul>
                                    <li>
                                        The baseline thresholds for consistency, interaction, and efficiency have been
                                        adjusted based on the output from the new card scoring system.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Power Level requirements
                                <ul>
                                    <li>
                                        Several baseline requirements have been added to gatekeep higher power levels.
                                        The thinking is that realistically 7 and 8 are decks that are on their way to
                                        being cEDH, so they need to be oriented more towards a competitive play style.
                                        <ul>
                                            <li>
                                                Power Level 7+
                                                <ul>
                                                    <li>Must include some form of combo</li>
                                                    <li>Must have a manafixing score greater than or equal to 80</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                        <p />
                        Detection of the following card categories has been improved:
                        <ul>
                            <li>Removal</li>
                            <li>Draw</li>
                            <li>Ramp</li>
                            <li>Recursion</li>
                            <li>Groupslug</li>
                            <li>Stax</li>
                            <li>Taxes</li>
                        </ul>
                        <div style={{ height: 30 }} />
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.7 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>cEDH Meta</span>
                        <ul>
                            <li>Meta updated</li>
                            <li>Staples list updated</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Discord</span>
                        <p />
                        <ul>
                            <li>
                                The "/check" command now returns a summary image
                                <ul>
                                    <li>
                                        <img src={'/resources/discord_banner_preview.png'} />
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.6 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Deck Importing</span>
                        <p />
                        <ul>
                            <li>
                                Archidekt
                                <ul>
                                    <li>Fixed issue wherein sideboards were included in card aggregation</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.5 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <p />
                        <ul>
                            <li>
                                Interaction
                                <ul>
                                    <li>Taxes are now included in the calculations for interaction</li>
                                    <li>Interaction piece scores are now weighted by whether or not they are 'slow'</li>
                                </ul>
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>UI</span>
                        <ul>
                            <li>
                                Power Levels
                                <ul>
                                    <li>Now displaying Tax effects in list of cards for power level scoring</li>
                                    <li>Standardizing columns/fields in list shown between cEDH and Casual views</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.4 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Categories</span>
                        <p />
                        <Text>Detection algorithms for the following categories have been improved:</Text>
                        <ul>
                            <li>Cheat</li>
                            <li>Counterspells</li>
                            <li>Evasion</li>
                            <li>Ramp</li>
                            <li>Removal</li>
                            <li>Stax</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.3 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Categories</span>
                        <p />
                        <ul>
                            <li>Ramp - mana rocks are once again being listed</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Deck importing</span>
                        <p />
                        <ul>
                            <li>mtggoldfish.com - fixed import error with decks from this site</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.2 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Categories</span>
                        <p />
                        <Text>Detection algorithms for the following categories have been improved:</Text>
                        <ul>
                            <li>Draw</li>
                            <li>Evasion</li>
                            <li>Fastmana</li>
                            <li>Grouphug</li>
                            <li>Groupslug</li>
                            <li>Ramp</li>
                            <li>Removal</li>
                            <li>Stax</li>
                            <li>Tutor</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.1 (incremental)" isOpen={false}>
                    <Text>
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <ul>
                            <li>Capped 'stax weighting' bonuses</li>
                        </ul>
                    </Text>
                    <Text>
                        The stax weighting bonus is meant to bolster deck scores that are more reliant on stax than
                        combo to win. This was necessary in earlier versions of the app because combo was weighted more
                        heavily. However, the scoring algorithm appears to be more accurate now, and Winota decks were
                        getting higher scores than their meta share would dictate.
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 11022023.0" isOpen={false}>
                    <Text>
                        <p />
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <ul>
                            <li>
                                Massive reworking of synergy lookups. The following should be more correct:
                                <ul>
                                    <li>Card type matches</li>
                                    <li>Enablers</li>
                                    <li>Triggers found</li>
                                </ul>
                            </li>
                        </ul>
                    </Text>
                    <Text>
                        It is possible that I broke some synergies that I am not aware of. If you spot something, feel
                        free to let me know in the discord!
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 10072023.1 (incremental)" isOpen={false}>
                    <Text>
                        <p />
                        <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                        <ul>
                            <li>
                                Cost reduction synergy has been added
                                <ul>
                                    <li>Checks color, permanent type, etc</li>
                                    <li>Only includes cards that could actually be reduced</li>
                                    <ul>
                                        <li>eg Sapphire Medallion does not reduce Mystic Remora</li>
                                    </ul>
                                </ul>
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Manabase Analysis</span>
                        <ul>
                            <li>Added bonus and cmc adjustments for cost reduction effects</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                        <br />
                        <ul>
                            <li>
                                Cheat
                                <ul>
                                    <li>
                                        Rules expanded to include cascade as well as effects like Etali, Primal
                                        Conqueror, Doors of Durin, Vorinclex // The Grand Evolution
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>UI</span>
                        <ul>
                            <li>Added cost reduction information to Manabase tab.</li>
                        </ul>
                    </Text>
                </ChangelogPane>
                <ChangelogPane title="Platform Release: 10072023.0" isOpen={false}>
                    <Text>
                        This update is primarily focused on correcting decks whose Power Levels were rated higher than
                        they should be.
                        <p />
                        <span style={{ fontWeight: 'bolder' }}>Manabase Analysis</span>
                        <br />
                        This new set of rules look at the following criteria when reading a deck:
                        <ul>
                            <li>Base land quantity</li>
                            <li>Total mana production relative to the average CMC of nonland cards</li>
                            <li>
                                Ratio of colored mana production relative to pip density in cards for each color in the
                                deck
                            </li>
                            <li>Presence of fetches</li>
                            <li>Whether or not the land/rock/dork ETBs tapped</li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                        <br />
                        The following categories have been added:
                        <ul>
                            <li>
                                Pregame
                                <ul>
                                    <li>This covers effects like Leyline of Anticipation, Gemstone Caverrns, etc</li>
                                </ul>
                            </li>
                        </ul>
                        And these have been updated and improved:
                        <ul>
                            <li>
                                Slow
                                <ul>
                                    <li>Detection of ETB Tapped and such should be better now</li>
                                </ul>
                            </li>
                            <li>
                                Cheat
                                <ul>
                                    <li>
                                        Better detection of effects that allow one to cheat cards in to play. Cards like
                                        Tooth and Nail should now be accounted for
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <div style={{ height: 30 }} />
                        <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                        <br />
                        This has been tweaked in the following ways:
                        <ul>
                            <li>
                                Manabase score is now much more heavily weighted when generating a power level score
                            </li>
                            <li>
                                Decks that have less than 10 mana-producing lands are now automatically a 1.1.
                                <ul>
                                    <li>
                                        There are a lot of decks on the site currently that tried to get the highest
                                        power level and salt level possible by making unplayable decks
                                    </li>
                                    <li>
                                        The baseline amount of 10 could be too severe - I will increase it if I get
                                        feedback requesting it
                                    </li>
                                    <li>
                                        This override score supersedes any power level that would have been otherwise
                                        generated
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Decks that have a power level rating less than 5 get a boost based on their synergy
                                score
                                <ul>
                                    <li>
                                        The synergy tool is very much still a work in progress, and I am leery of
                                        rushing and having it affect all decks all at once.
                                    </li>
                                    <li>
                                        When looking at the deck scores, it seems that a lot of the decks that are under
                                        5 are more synergy focused
                                    </li>
                                    <li>
                                        Additionally, most of the cEDH lists I looked at appeared to have much lower
                                        synergy scores, which surprised me at first. However it does seem to make sense
                                        - those decks often tend to be "value piles" aimed at being as efficient as
                                        possible.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <span style={{ fontWeight: 'bolder' }}>UI</span>
                        <br />
                        The website UI has been updated:
                        <ul>
                            <li>
                                New Manabase Analysis section and score. This UI is still early in the development stage
                                and will be incrementally updated
                            </li>
                            <li>
                                Lists of individual card scores for Landbase and Ramp have been removed from the Power
                                Levels tab. Please refer to the new Manabase tab for this information
                            </li>
                        </ul>
                    </Text>
                </ChangelogPane>
            </Flex>
        </Flex>
    );
}
